<template>
  <div class="page-container">
    <div class="settings-form">
      <h2 class="page-title">图库管理</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.tuku_name" placeholder="请输入图库名称" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增图库
        </el-button>
      </div>

      <!-- 图库列表 -->
      <el-table :data="galleryList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column prop="tuku_name" label="图库名称" width="200" />
        <!-- 新增的两个表头 -->
        <el-table-column label="素材总数" width="120">
          <template #default="scope">
            <el-tag>{{ scope.row.total_count || 0 }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="未使用数" width="120">
          <template #default="scope">
            <el-tag :type="scope.row.unused_count > 0 ? 'warning' : 'success'">
              {{ scope.row.unused_count || 0 }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="notes" label="备注" min-width="150" show-overflow-tooltip />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="操作" width="290" fixed="right">
          <template #default="scope">
            <el-button type="info" size="small" @click="openMaterialDialog(scope.row)">
              <el-icon>
                <Picture />
              </el-icon>
              查看素材
            </el-button>
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchGalleryList" @current-change="fetchGalleryList" />
      </div>
    </div>

    <!-- 新增/编辑图库对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" :close-on-click-modal="false">
      <el-form ref="galleryForm" :model="galleryForm" label-width="120px" :rules="formRules">
        <el-form-item label="图库名称" prop="tuku_name">
          <el-input v-model="galleryForm.tuku_name" placeholder="请输入图库名称" size="large" />
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="galleryForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitGalleryForm">确认</el-button>
      </template>
    </el-dialog>

    <!-- 图片素材管理对话框 -->
    <el-dialog v-model="materialDialogVisible" :title="materialDialogTitle" width="60%" top="5vh"
      :close-on-click-modal="false">
      <!-- 图片素材搜索 -->
      <div class="search-bar material-search">
        <el-input v-model="materialSearchForm.tp_name" placeholder="请输入图片名称" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleMaterialSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleMaterialSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索图片
        </el-button>

        <el-button type="success" size="large" @click="openAddMaterialDialog" style="margin-right: 10px">
          <el-icon>
            <Plus />
          </el-icon>
          新增图片
        </el-button>

        <el-button type="warning" size="large" @click="triggerBatchUpload">
          <el-icon>
            <Upload />
          </el-icon>
          批量上传
        </el-button>
        <input type="file" ref="batchUploadInput" style="display: none" multiple accept="image/*"
          @change="handleBatchUpload" />
      </div>

      <!-- 图片素材列表 -->
      <div class="material-list">
        <div v-for="item in materialList" :key="item.id" class="material-item">
          <el-image :src="item.tp_url" :preview-src-list="previewList" :initial-index="materialList.indexOf(item)"
            fit="cover" class="material-image" :title="item.tp_name" />
          <div class="material-info">
            <div class="material-name">{{ item.tp_name || '未命名图片' }}</div>
            <div class="material-actions">
              <el-tag size="small">使用次数: {{ item.use_num }}</el-tag>
              <el-button type="primary" size="small" @click="openEditMaterialDialog(item)">
                <el-icon>
                  <Edit />
                </el-icon>
                编辑
              </el-button>
              <el-button type="danger" size="small" @click="handleMaterialDelete(item.id)">
                <el-icon>
                  <Delete />
                </el-icon>
                删除
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 素材分页 -->
      <div class="pagination-container" v-show="materialPagination.total > 0">
        <el-pagination v-model:current-page="materialPagination.page" v-model:page-size="materialPagination.pageSize"
          :total="materialPagination.total" :page-sizes="[6, 12, 24, 36]"
          layout="total, sizes, prev, pager, next, jumper" @size-change="fetchMaterialList"
          @current-change="fetchMaterialList" />
      </div>

      <!-- 图片素材为空提示 -->
      <el-empty v-if="materialList.length === 0" description="暂无图片素材" />
    </el-dialog>

    <!-- 新增/编辑图片素材对话框 -->
    <el-dialog v-model="materialFormDialogVisible" :title="materialFormDialogTitle" width="600px"
      :close-on-click-modal="false">
      <el-form ref="materialForm" :model="materialForm" label-width="120px">
        <el-form-item label="图片名称">
          <el-input v-model="materialForm.tp_name" placeholder="请输入图片名称" size="large" />
        </el-form-item>

        <!-- 替换原有的图片地址输入框 -->
        <el-form-item label="上传图片" prop="tp_url">
          <el-upload class="upload-demo" :auto-upload="false" :show-file-list="false" :on-change="handleImageChange"
            accept="image/*">
            <el-button type="primary">选择图片</el-button>
            <template #tip>
              <div class="el-upload__tip">
                请上传图片文件
              </div>
            </template>
          </el-upload>
          <div v-if="materialForm.tp_url" class="upload-preview">
            已上传图片: {{ materialForm.tp_url }}
          </div>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="materialForm.notes" placeholder="请输入备注" type="textarea" :rows="3" />
        </el-form-item>

        <el-form-item label="图片预览" v-if="materialForm.tp_url">
          <el-image :src="materialForm.tp_url" class="preview-image" :preview-src-list="[materialForm.tp_url]"
            fit="contain" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="materialFormDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitMaterialForm">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, Picture, Upload
} from '@element-plus/icons-vue'
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils';

export default {
  name: 'MaterialGallery',
  components: { Search, Plus, Edit, Delete, Picture, Upload },
  data() {
    return {
      loading: false,
      // 图库相关
      dialogVisible: false,
      dialogTitle: '新增图库',
      isEditMode: false,
      searchForm: {
        tuku_name: ''
      },
      galleryForm: {
        id: null,
        tuku_name: '',
        notes: ''
      },
      formRules: {
        tuku_name: [
          { required: true, message: '请输入图库名称', trigger: 'blur' }
        ]
      },
      galleryList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },

      // 图片素材相关
      materialDialogVisible: false,
      materialFormDialogVisible: false,
      materialDialogTitle: '图片素材管理',
      materialFormDialogTitle: '新增图片素材',
      materialSearchForm: {
        tp_name: ''
      },
      materialForm: {
        id: null,
        tp_name: '',
        tp_url: '',
        use_num: 0,
        notes: '',
        tuku_id: null,
        store_id: null
      },
      materialList: [],
      materialPagination: {
        page: 1,
        pageSize: 6,
        total: 0
      },
      currentGallery: {}, // 当前操作的图库
      materialLoading: false,
      isMaterialEditMode: false
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },

    previewList() {
      return this.materialList.map(item => item.tp_url)
    }
  },
  mounted() {
    this.fetchGalleryList();
  },
  methods: {


    // 获取图库列表
    async fetchGalleryList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.tuku_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        // 如果有搜索条件，加入搜索参数
        if (this.searchForm.tuku_name) {
          params.tuku_name = this.searchForm.tuku_name;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/library_management.php`,
          params
        );

        if (data.data) {
          this.galleryList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
          // 获取每个图库的统计数据
          await this.fetchGalleryStats();
        }
      } catch (error) {
        this.$message.error('获取图库列表失败');
        console.error('获取图库列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 新增方法：获取图库统计数据
    async fetchGalleryStats() {
      try {
        // 并行获取所有图库的统计数据
        const statsPromises = this.galleryList.map(gallery =>
          axios.post(`${config.BASE_URL}/shop/api/library_management.php`, {
            action: 'stats',
            store_id: this.userInfo.id,
            tuku_id: gallery.id
          }).catch(() => null) // 防止单个请求失败影响其他
        );

        const statsResults = await Promise.all(statsPromises);

        // 合并统计数据到图库列表
        this.galleryList = this.galleryList.map((gallery, index) => {
          const stats = statsResults[index]?.data?.data || {};
          return {
            ...gallery,
            total_count: stats.total_count || 0,
            unused_count: stats.unused_count || 0
          };
        });
      } catch (error) {
        console.error('获取图库统计数据错误:', error);
      }
    },

    // 触发批量上传
    triggerBatchUpload() {
      this.$refs.batchUploadInput.click();
    },

    // 处理批量上传
    async handleBatchUpload(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      this.$loading({
        lock: true,
        text: `正在上传 ${files.length} 张图片...`,
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      try {
        // 并行上传所有图片
        const uploadPromises = Array.from(files).map(file => {
          return this.uploadImage(file)
            .then(url => ({
              tuku_id: this.currentGallery.id,
              store_id: this.userInfo.id,
              tp_url: url,
              tp_name: file.name.replace(/\.[^/.]+$/, ""), // 去除文件扩展名
              use_num: 0,
              notes: ''
            }))
            .catch(error => {
              // 捕获并返回错误信息，以便后续处理
              return {
                error: true,
                fileName: file.name,
                message: error.message || '上传失败'
              };
            });
        });

        // 等待所有上传完成
        const results = await Promise.all(uploadPromises);

        // 分离成功和失败的结果
        const successfulUploads = results.filter(item => !item.error);
        const failedUploads = results.filter(item => item.error);

        if (successfulUploads.length === 0 && failedUploads.length > 0) {
          // 所有上传都失败
          const errorMessages = failedUploads.map(f => `${f.fileName}: ${f.message}`).join('\n');
          throw new Error(`所有图片上传失败:\n${errorMessages}`);
        }

        // 如果有部分失败，显示失败信息
        if (failedUploads.length > 0) {
          const errorMessages = failedUploads.map(f => `${f.fileName}: ${f.message}`).join('\n');
          this.$message.warning(`${failedUploads.length} 张图片上传失败:\n${errorMessages}`);
        }

        // 批量保存到数据库
        if (successfulUploads.length > 0) {
          await this.saveBatchMaterials(successfulUploads);
          this.$message.success(`成功上传 ${successfulUploads.length}/${files.length} 张图片`);
          this.fetchMaterialList();
          this.fetchGalleryStats();
        }
      } catch (error) {
        this.$message.error(`批量上传失败: ${error.message}`);
        console.error('批量上传错误:', error);
      } finally {
        this.$loading().close();
        // 清空文件输入，以便重复选择相同文件
        event.target.value = '';
      }
    },


    // 批量保存素材到数据库
    async saveBatchMaterials(materials) {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/picture_materials.php`,
          {
            action: 'add',
            batch: materials,
            store_id: this.userInfo.id
          }
        );

        if (!data || !data.success) {
          throw new Error(data.error || '批量保存失败');
        }
      } catch (error) {
        console.error('批量保存错误:', error);
        throw error;
      }
    },
    // 处理图片选择变化
    async handleImageChange(file) {
      try {
        this.$message.info('图片上传中，请稍候...');
        const url = await this.uploadImage(file.raw);
        this.materialForm.tp_url = url;
        this.$message.success('图片上传成功');
      } catch (error) {
        // 显示具体的错误信息
        this.$message.error(`图片上传失败: ${error.message}`);
        console.error('上传错误:', error);
      }
    },
    // 优化后的上传方法
    async uploadImage(file) {
      try {
        // 显示上传进度
        const loading = this.$loading({
          lock: true,
          text: '图片上传中...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        });

        // 在调用上传前先验证文件
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
        const maxSize = 2 * 1024 * 1024; // 2MB

        if (allowedTypes.indexOf(file.type) === -1) {
          throw new Error(`不支持的文件类型: ${file.type}，仅支持 ${allowedTypes.join(', ')}`);
        }

        if (file.size > maxSize) {
          throw new Error(`文件大小超过限制 (2MB): ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        }

        const signature = await getUploadSignature(file.type.split('/')[0]);
        const res = await uploadToOSS(file, signature);

        loading.close();
        return res.url;
      } catch (error) {
        this.$loading().close();
        console.error('图片上传失败:', error);
        throw error; // 重新抛出错误，让调用者处理
      }
    },
    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchGalleryList();
    },

    // 打开新增图库对话框
    openAddDialog() {
      this.dialogTitle = '新增图库';
      this.isEditMode = false;
      this.galleryForm = {
        id: null,
        tuku_name: '',
        notes: ''
      };
      this.dialogVisible = true;
    },

    // 打开编辑图库对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑图库';
      this.isEditMode = true;
      this.galleryForm = {
        id: row.id,
        tuku_name: row.tuku_name,
        notes: row.notes
      };
      this.dialogVisible = true;
    },

    // 提交图库表单（新增/编辑）
    submitGalleryForm() {
      this.$refs.galleryForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = {
              ...this.galleryForm,
              store_id: this.userInfo.id
            };

            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/library_management.php`,
              {
                action,
                ...payload
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchGalleryList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('图库表单提交错误:', error);
          }
        }
      });
    },

    // 删除图库
    async handleDelete(id) {
      this.$confirm('确定要删除该图库吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/library_management.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchGalleryList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('图库删除错误:', error);
        }
      }).catch(() => { });
    },

    /****************************** 图片素材管理部分 ******************************/

    // 打开图片素材对话框
    openMaterialDialog(gallery) {
      this.currentGallery = gallery;
      this.materialDialogTitle = `${gallery.tuku_name} - 图片素材管理`;
      this.materialDialogVisible = true;

      // 重置搜索条件
      this.materialSearchForm.tp_name = '';
      this.materialPagination.page = 1;

      // 加载素材列表
      this.fetchMaterialList();
      this.fetchGalleryStats();
    },

    // 获取图片素材列表
    async fetchMaterialList() {
      this.materialLoading = true;
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.id,
          tuku_id: this.currentGallery.id,
          page: this.materialPagination.page,
          page_size: this.materialPagination.pageSize
        };

        // 如果有搜索条件
        if (this.materialSearchForm.tp_name) {
          params.action = 'search';
          params.tp_name = this.materialSearchForm.tp_name;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/picture_materials.php`,
          params
        );

        if (data && data.data) {
          this.materialList = data.data;
          this.materialPagination = {
            page: data.pagination?.page || this.materialPagination.page,
            pageSize: data.pagination?.page_size || this.materialPagination.pageSize,
            total: data.pagination?.total || data.data.length
          };
        }
      } catch (error) {
        this.$message.error('获取图片素材失败');
        console.error('获取图片素材错误:', error);
      } finally {
        this.materialLoading = false;
      }
    },

    // 图片素材搜索
    handleMaterialSearch() {
      this.materialPagination.page = 1;
      this.fetchMaterialList();
      this.fetchGalleryStats();

    },

    // 打开新增图片素材对话框
    openAddMaterialDialog() {
      this.materialFormDialogTitle = '新增图片素材';
      this.isMaterialEditMode = false;
      this.materialForm = {
        id: null,
        tp_name: '',
        use_num: 0,
        tp_url: '',
        notes: '',
        tuku_id: this.currentGallery.id,
        store_id: this.userInfo.id
      };
      this.materialFormDialogVisible = true;
    },

    // 打开编辑图片素材对话框
    openEditMaterialDialog(item) {
      this.materialFormDialogTitle = '编辑图片素材';
      this.isMaterialEditMode = true;
      this.materialForm = {
        id: item.id,
        tp_name: item.tp_name,
        tp_url: item.tp_url,
        notes: item.notes,
        tuku_id: this.currentGallery.id,
        store_id: this.userInfo.id
      };
      this.materialFormDialogVisible = true;
    },

    // 提交图片素材表单
    async submitMaterialForm() {
      try {
        const action = this.isMaterialEditMode ? 'update' : 'add';
        const formData = { ...this.materialForm };

        // 删除不必要的字段
        delete formData.create_time;
        delete formData.update_time;

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/picture_materials.php`,
          {
            action,
            ...formData
          }
        );

        if (data && data.success) {
          this.$message.success(this.isMaterialEditMode ? '图片更新成功' : '图片添加成功');
          this.materialFormDialogVisible = false;
          this.fetchMaterialList();
          this.fetchGalleryStats();

        } else {
          this.$message.error(data.error || '操作失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '操作失败';
        this.$message.error(`操作失败: ${errorMsg}`);
        console.error('图片素材表单提交错误:', error);
      }
    },

    // 删除图片素材
    handleMaterialDelete(id) {
      this.$confirm('确定要删除该图片素材吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/picture_materials.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data && data.success) {
            this.$message.success('删除成功');
            this.fetchMaterialList();
            this.fetchGalleryStats();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('图片素材删除错误:', error);
        }
      }).catch(() => { });
    },

    // 批量上传成功处理
    handleUploadSuccess(response) {
      if (response.success) {
        this.$message.success(`成功上传 ${response.data.length} 张图片`);
        this.fetchMaterialList();
        this.fetchGalleryStats();
      } else {
        this.$message.error(response.error || '上传失败');
      }
    },

    // 批量上传错误处理 - 移除未使用的 file 和 fileList 参数
    handleUploadError(error) {
      this.$message.error(`上传失败: ${error.message}`);
    }
  }
}
</script>

<style scoped lang="scss">
.page-container {
  padding: 0px;
}

.settings-form {
  max-width: 95%;
  margin: 0 auto;
  padding: 32px 40px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;

  &.material-search {
    justify-content: space-between;
    flex-wrap: wrap;
  }
}

.inline-upload {
  display: inline-block;
  margin-left: 10px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

/* 表格样式优化 */
.el-table {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }

  :deep(.el-tag) {
    margin: 2px 0;
    font-size: 12px;
    min-width: 40px;
    text-align: center;
  }

  :deep(.cell) {
    display: flex;
    justify-content: center;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}

/* 图片素材相关样式 */
.material-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.material-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;

  &:hover {

    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }
}


.material-image {
  width: 100%;
  height: 200px;
  display: block;
  cursor: pointer;
  background-color: #f5f7fa;
}

.material-info {
  padding: 12px;

  .material-name {
    font-weight: 500;
    margin-bottom: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .material-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  display: block;
  margin: 10px auto;
  border-radius: 4px;
}

.upload-demo {
  width: 100%;
}

.upload-preview {
  margin-top: 10px;
  padding: 8px;
  background-color: #f5f7fa;
  border-radius: 4px;
  word-break: break-all;
  font-size: 14px;
  color: #606266;
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  display: block;
  margin: 10px auto;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}
</style>